export type ScannerSource =
  | 'keyboard'
  | 'manual'
  | 'native-bridge'
  | 'zebra-datawedge'
  | 'honeywell-bridge'
  | 'datalogic-bridge'
  | 'webhid'
  | 'webusb'
  | 'webserial';

export type ScannerMode =
  | 'setup'
  | 'asn-detect'
  | 'trip-audit'
  | 'blind-trip-audit'
  | 'store-audit'
  | 'pallet-list'
  | 'pallet-products'
  | 'barcode-list';

export type ScanOutcome = 'success' | 'duplicate' | 'warning' | 'error' | 'info';

export interface ScanEvent {
  value: string;
  symbology?: string;
  source: ScannerSource;
  raw?: unknown;
  timestamp: number;
}

export interface ScannerAdapterStatus {
  source: ScannerSource;
  label: string;
  available: boolean;
  active: boolean;
  detail: string;
}

export interface ScannerContextResponse {
  domain_uuid?: string | null;
  pool_point_uuid?: string | null;
  serviced_stores?: unknown;
  pool_clients?: unknown;
}

export interface ScannerSessionState {
  domainUuid: string;
  poolPointUuid: string;
  poolPointClientUuid: string;
  poolPointClientName: string;
  asnNumber: string;
  shipmentListUuid: string;
  tripNumber: string;
  tripListUuid: string;
  trailerNumber: string;
  storeNumber: string;
  poolPointStoreUuid: string;
  servicedStores: string[];
}

export interface PoolClient {
  pool_point_client_uuid: string;
  client_name?: string;
  pool_point_client_name?: string;
  company_name?: string;
  name?: string;
  [key: string]: unknown;
}

export interface StoreOption {
  store_number: string;
  shipment_list_uuid?: string;
  pool_point_store_uuid?: string;
  pool_point_uuid?: string;
  [key: string]: unknown;
}

export interface TripLookup {
  trip_number?: string;
  blind_trip_number?: string;
  trip_list_uuid?: string;
  shipment_list_uuid?: string;
  asn_number?: string;
  blind_scan?: boolean | string | number;
  message?: string;
  [key: string]: unknown;
}

export interface ProductRecord {
  product_uuid?: string;
  barcode?: string;
  store_number?: string;
  trip_number?: string;
  asn_number?: string;
  audited?: boolean | string | number;
  received_in?: boolean | string | number;
  package_uuid?: string;
  return_status?: string;
  resolution?: string;
  [key: string]: unknown;
}

export interface PalletRecord {
  package_uuid?: string;
  dimension_uuid?: string;
  store_number?: string;
  carton_count?: string | number;
  load_weight?: string | number;
  dimension_height?: string | number;
  dimension_length?: string | number;
  dimension_width?: string | number;
  [key: string]: unknown;
}

export interface ScannerApiEnvelope<T = unknown> {
  action: string;
  params?: Record<string, unknown>;
  body?: Record<string, unknown>;
}

export interface ScanLogEntry {
  barcode: string;
  source: ScannerSource;
  mode: ScannerMode;
  outcome: ScanOutcome;
  message: string;
  timestamp: Date;
}

export interface CreatePalletFormValue {
  height: number | null;
  length: number | null;
  width: number | null;
  weight: number | null;
}

export const DAMAGE_RESOLUTIONS = [
  'DELIVERED AS IS',
  'HELD FOR DISPOSITION',
  'BARCODE DAMAGED',
  'BARCODE ILLEGIBLE',
  'LABEL MISSING',
  'RETAPED AND DELIVERED',
  'REPACKAGED AND DELIVERED',
  'DISCARDED DISPOSED',
] as const;

export type DamageResolution = typeof DAMAGE_RESOLUTIONS[number];

export const SCANNER_SESSION_STORAGE_KEY = 'barcodeScannerSessionState';

export const EMPTY_SCANNER_SESSION: ScannerSessionState = {
  domainUuid: '',
  poolPointUuid: '',
  poolPointClientUuid: '',
  poolPointClientName: '',
  asnNumber: '',
  shipmentListUuid: '',
  tripNumber: '',
  tripListUuid: '',
  trailerNumber: '',
  storeNumber: '',
  poolPointStoreUuid: '',
  servicedStores: [],
};
